<template>
  <div>
    <div class="card-body">
      <h5 class="card-title" v-show="title">{{ title }}</h5>
      <h6 class="card-subtitle mb-2 text-muted" v-show="subTitle">{{ subTitle }}</h6>
      <table class="table table-sm table-hover">
        <thead class="thead-light">
          <tr>
            <th v-for="column in columns" :key="column">{{ column }}</th>
            <th v-if="hasActions">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in results"
            :key="index"
            :role="isServiceOrderRow(row) ? 'button' : null"
            @click="handleRowClick(row)"
            @keydown.enter="handleRowClick(row)"
          >
            <td v-for="column in columns" :key="column">{{ row[column] }}</td>
            <td
              v-if="hasActions && shouldShowActionCell(row, index)"
              :rowspan="getActionRowspan(row, index)"
              @click.stop
              style="vertical-align: middle"
            >
              <button v-if="row.Action" class="btn btn-sm btn-primary" @click="executeCommandAsync(row)">Označi</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, toRef, toRefs, computed, defineComponent, watch, reactive, onMounted, inject } from "vue"
import { listEntitiesAsync } from "scripts/api"
import { useAuth } from "../composition-api/useAuth"
import { setError, setSuccess, startLoading, stopLoading } from "scripts/components/Notification"

defineOptions({ name: "UniversalCard" })

const props = defineProps({
  url: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: false,
  },
  subTitle: {
    type: String,
    required: false,
  },
})

const router = VueRouter.useRouter()
const { fetchAuthAsync } = useAuth()
const results = ref([])
const columns = ref([])

const hasActions = computed(() => {
  return results.value?.some((row) => row.Action != null)
})

const shouldShowActionCell = (row, index) => {
  // Show on first row or when ServiceOrderID changes from previous row
  return index === 0 || results.value[index - 1]?.ServiceOrderID !== row.ServiceOrderID
}

const getActionRowspan = (row, index) => {
  // Count consecutive rows with same ServiceOrderID
  let count = 1
  for (let i = index + 1; i < results.value.length; i++) {
    if (results.value[i]?.ServiceOrderID === row.ServiceOrderID) {
      count++
    } else {
      break
    }
  }
  return count
}

const loadEntities = async () => {
  results.value = await listEntitiesAsync(fetchAuthAsync, props.url, {})
  if (results.value?.length > 0) {
    columns.value = Object.keys(results.value[0]).filter(
      (column) => column !== "ID" && column !== "ServiceOrderID" && column !== "Action",
    )
  }
}

const getOrderHref = (order) => {
  const routeData = router.resolve({ name: "ServiceOrder", params: { workOrderId: order["ID"] } })
  return routeData.href
}

const isServiceOrderRow = (row) => row?.ServiceOrderID != null

const executeCommandAsync = async (row) => {
  if (!row.Action || !row.ServiceOrderID) {
    console.error("Missing Action or ServiceOrderID")
    return false
  }
  let result = null
  startLoading(`executeCommand ${row.Action}`)
  try {
    const raw = await fetchAuthAsync(`${AZURE_URI}/ServiceOrder/Command`, "POST", {
      ServiceOrderId: row.ServiceOrderID,
      Type: row.Action,
    })
    if (raw?.status !== "ok") {
      throw raw
    }
    setSuccess("Done!")
  } catch (e) {
    setError(e)
  }
  await loadEntities()
  stopLoading(`executeCommand ${row.Action}`)
  return result
}

const handleRowClick = (row) => {
  // Navigate if it's a service order row
  if (!isServiceOrderRow(row)) {
    return
  }

  router.push({ name: "ServiceOrder", params: { workOrderId: row.ServiceOrderID } })
}

onMounted(async () => {
  await loadEntities()
})
</script>
